import React, { useEffect, useState, lazy } from 'react';
import { CheckCircle2, XCircle, ShieldCheck, Star, ChevronDown, Brain, Clock, BookOpen } from 'lucide-react';
import { IMAGES, goToCheckout } from './lib/constants';
import { CtaButton } from './components/CtaButton';
import { Reveal } from './components/Reveal';
import { StickyCta } from './components/StickyCta';
import { motion, AnimatePresence } from 'framer-motion';
function SalesPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [buyer, setBuyer] = useState({
    name: '',
    country: '',
    time: ''
  });
  const buyers = [{
    name: 'María C.',
    country: 'México',
    time: 'hace 5 minutos'
  }, {
    name: 'Carlos R.',
    country: 'Colombia',
    time: 'hace 12 minutos'
  }, {
    name: 'Laura S.',
    country: 'España',
    time: 'hace 2 minutos'
  }, {
    name: 'Andrea M.',
    country: 'Argentina',
    time: 'hace 8 minutos'
  }, {
    name: 'José P.',
    country: 'Chile',
    time: 'hace 15 minutos'
  }];
  useEffect(() => {
    const showPopup = () => {
      const randomBuyer = buyers[Math.floor(Math.random() * buyers.length)];
      setBuyer(randomBuyer);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    const initialTimer = setTimeout(showPopup, 10000);
    const intervalTimer = setInterval(showPopup, 35000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-0">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.9
    }} className="fixed bottom-24 left-4 right-4 z-50 flex max-w-sm items-center gap-4 rounded-2xl bg-white p-4 shadow-float border border-brand-soft sm:left-6 sm:right-auto" data-id="element-1">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-rosa text-brand-purple" data-id="element-2">
            <CheckCircle2 className="h-6 w-6" data-id="element-3" />
          </div>
          <div data-id="element-4">
            <p className="text-sm font-medium text-gray-900" data-id="element-5">
              {buyer.name} de {buyer.country}
            </p>
            <p className="text-xs text-gray-500" data-id="element-6">
              Compró el Protocolo Antibloqueo {buyer.time}
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
function AccordionItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 py-4" data-id="element-7">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between text-left focus:outline-none" data-id="element-8">
        <span className="font-semibold text-gray-900" data-id="element-9">{question}</span>
        <ChevronDown className={`h-5 w-5 text-brand-purple transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-10" />
      </button>
      <AnimatePresence data-id="element-11">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-12">
            <p className="pt-4 text-gray-600" data-id="element-13">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function App() {
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-brand-lila selection:text-white pb-24 sm:pb-0" data-id="element-14">
      <SalesPopup data-id="element-15" />
      <StickyCta data-id="element-16" />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-rosa/30 to-white pt-20 pb-24 lg:pt-32 lg:pb-32" data-id="element-17">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-18">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center" data-id="element-19">
            <Reveal data-id="element-20">
              <div className="max-w-2xl" data-id="element-21">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-brand-purple/10 px-4 py-2 text-sm font-semibold text-brand-purple" data-id="element-22">
                  <Brain className="h-4 w-4" data-id="element-23" />
                  SISTEMA CLÍNICO DE RESPUESTA INMEDIATA
                </div>
                <h1 className="font-display text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl" data-id="element-24">
                  Nunca vuelvas a quedarte en blanco durante una sesión.
                </h1>

                {/* Mobile Image (shows only on small screens) */}
                <div className="mt-8 block lg:hidden" data-id="element-25">
                  <img src={IMAGES.principal} alt="Protocolo Antibloqueo del Terapeuta" className="w-full drop-shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform duration-300" onClick={goToCheckout} data-id="element-26" />
                </div>

                <p className="mt-6 text-lg leading-8 text-gray-600" data-id="element-27">
                  Descubre el sistema práctico que te dice exactamente qué
                  preguntar, qué decir y qué intervención aplicar cuando una
                  sesión se bloquea.
                </p>
                <ul className="mt-8 space-y-4" data-id="element-28">
                  {['Intervenciones listas para aplicar', 'Más seguridad clínica', 'Ahorra horas preparando sesiones', 'Acceso inmediato'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-29">
                      <CheckCircle2 className="h-6 w-6 text-brand-purple shrink-0" data-id="element-30" />
                      {item}
                    </li>)}
                </ul>
                <div className="mt-10" data-id="element-31">
                  <CtaButton size="xl" pulse fullWidth className="sm:w-auto" data-id="element-32">
                    👉 QUIERO EL SISTEMA AHORA
                  </CtaButton>
                </div>
                <div className="mt-8 flex items-center gap-4 text-sm font-medium text-gray-500" data-id="element-33">
                  <div className="flex text-brand-gold" data-id="element-34">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-current" data-id="element-35" />)}
                  </div>
                  <span data-id="element-36">Más de 8 recursos clínicos incluidos</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2} className="hidden lg:block" data-id="element-37">
              <div className="relative" data-id="element-38">
                <div className="absolute -inset-4 rounded-full bg-brand-rosa/50 blur-3xl" data-id="element-39" />
                <img src={IMAGES.principal} alt="Protocolo Antibloqueo del Terapeuta" className="relative z-10 w-full drop-shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform duration-500" onClick={goToCheckout} data-id="element-40" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="bg-gray-50 py-24 sm:py-32" data-id="element-41">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-42">
          <Reveal data-id="element-43">
            <div className="mx-auto max-w-2xl text-center" data-id="element-44">
              <h2 className="font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-45">
                ¿Por qué tantos terapeutas sienten que improvisan?
              </h2>
            </div>
          </Reveal>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2 lg:max-w-none lg:grid-cols-4" data-id="element-46">
            {[{
            icon: '😰',
            title: 'El paciente deja de hablar…',
            desc: 'Y no sabes cómo continuar.'
          }, {
            icon: '🤯',
            title: 'Conoces mucha teoría…',
            desc: 'Pero no sabes qué técnica aplicar.'
          }, {
            icon: '⏳',
            title: 'Pierdes mucho tiempo',
            desc: 'preparando sesiones.'
          }, {
            icon: '😓',
            title: 'Sales pensando…',
            desc: '“Pude haber hecho una mejor intervención.”'
          }].map((card, i) => <Reveal key={i} delay={i * 0.1} data-id="element-47">
                <div className="flex h-full flex-col rounded-3xl bg-white p-8 shadow-card ring-1 ring-gray-900/5 transition-all hover:-translate-y-1 hover:shadow-float" data-id="element-48">
                  <div className="mb-4 text-4xl" data-id="element-49">{card.icon}</div>
                  <h3 className="mb-2 font-semibold text-gray-900" data-id="element-50">
                    {card.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-51">{card.desc}</p>
                </div>
              </Reveal>)}
          </div>
          <Reveal delay={0.4} data-id="element-52">
            <div className="mx-auto mt-16 max-w-3xl rounded-3xl bg-brand-purple p-8 text-center shadow-float sm:p-12" data-id="element-53">
              <p className="text-xl font-medium text-white sm:text-2xl" data-id="element-54">
                No necesitas estudiar más.
                <br data-id="element-55" />
                <span className="text-brand-rosa" data-id="element-56">
                  Necesitas un sistema que te diga exactamente cuál es el
                  siguiente paso.
                </span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-24 sm:py-32" data-id="element-57">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-58">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center" data-id="element-59">
            <Reveal data-id="element-60">
              <img src={IMAGES.principal} alt="Protocolo" className="w-full rounded-2xl shadow-2xl cursor-pointer" onClick={goToCheckout} data-id="element-61" />
            </Reveal>
            <Reveal delay={0.2} data-id="element-62">
              <h2 className="text-sm font-bold tracking-widest text-brand-purple uppercase" data-id="element-63">
                Presentamos
              </h2>
              <h3 className="mt-2 font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-64">
                PROTOCOLO ANTIBLOQUEO DEL TERAPEUTA™
              </h3>
              <p className="mt-6 text-lg text-gray-600" data-id="element-65">
                Un sistema clínico diseñado para ayudarte a intervenir con
                claridad, confianza y rapidez cuando una sesión se estanca.
              </p>
              <ul className="mt-8 space-y-4" data-id="element-66">
                {['Qué preguntar', 'Qué responder', 'Qué ejercicio aplicar', 'Cómo salir de un bloqueo', 'Cómo cerrar la sesión'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-67">
                    <CheckCircle2 className="h-6 w-6 text-brand-lila shrink-0" data-id="element-68" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-10" data-id="element-69">
                <CtaButton size="lg" pulse data-id="element-70">
                  QUIERO ACCESO INMEDIATO
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET SECTION */}
      <section className="bg-brand-soft py-24 sm:py-32" data-id="element-71">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-72">
          <Reveal data-id="element-73">
            <div className="mx-auto max-w-2xl text-center mb-16" data-id="element-74">
              <h2 className="font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-75">
                Todo lo que recibirás hoy
              </h2>
            </div>
          </Reveal>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-id="element-76">
            {/* Main Product */}
            <Reveal className="md:col-span-2 lg:col-span-3" data-id="element-77">
              <div className="flex flex-col md:flex-row items-center gap-8 rounded-4xl bg-white p-8 shadow-card ring-2 ring-brand-purple/20" data-id="element-78">
                <div className="w-full md:w-1/3" data-id="element-79">
                  <img src={IMAGES.principal} alt="Principal" className="w-full drop-shadow-xl cursor-pointer" onClick={goToCheckout} data-id="element-80" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-81">
                  <span className="inline-block rounded-full bg-brand-purple px-3 py-1 text-xs font-semibold text-white mb-4" data-id="element-82">
                    PRODUCTO PRINCIPAL
                  </span>
                  <h3 className="font-display text-2xl font-bold text-gray-900" data-id="element-83">
                    PROTOCOLO ANTIBLOQUEO DEL TERAPEUTA™
                  </h3>
                  <p className="mt-4 text-gray-600" data-id="element-84">
                    El sistema central para destrabar cualquier sesión al
                    instante.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Bonuses */}
            {[{
            title: 'Biblioteca de 200 Preguntas Terapéuticas',
            img: IMAGES.bono1
          }, {
            title: 'Scripts Clínicos para Salir del Bloqueo',
            img: IMAGES.bono2
          }, {
            title: 'Manual de Emergencias Terapéuticas',
            img: IMAGES.bono3
          }, {
            title: 'Checklist de Preparación en 5 Minutos',
            img: IMAGES.bono4
          }, {
            title: 'Framework de Decisiones Clínicas',
            img: IMAGES.bono5
          }, {
            title: 'Matriz de Autoevaluación Terapéutica',
            img: IMAGES.bono6
          }, {
            title: 'Cartas de Respuesta Instantánea',
            img: IMAGES.bono7
          }].map((bono, i) => <Reveal key={i} delay={i * 0.1} data-id="element-85">
                <div className="flex h-full flex-col rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-900/5 relative overflow-hidden group" data-id="element-86">
                  <div className="absolute top-4 right-4 rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700" data-id="element-87">
                    INCLUIDO GRATIS
                  </div>
                  <span className="text-sm font-bold text-brand-lila mb-2" data-id="element-88">
                    Bono {i + 1}
                  </span>
                  <h4 className="font-semibold text-gray-900 mb-4" data-id="element-89">
                    {bono.title}
                  </h4>
                  {bono.img ? <img src={bono.img} alt={bono.title} className="mt-auto w-full object-contain h-48 drop-shadow-lg group-hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={goToCheckout} data-id="element-90" /> : <div className="mt-auto w-full h-48 bg-brand-rosa/20 rounded-xl flex items-center justify-center border-2 border-dashed border-brand-lila/30" data-id="element-91">
                      <BookOpen className="h-12 w-12 text-brand-lila/50" data-id="element-92" />
                    </div>}
                </div>
              </Reveal>)}
          </div>

          <div className="mt-16 text-center" data-id="element-93">
            <CtaButton size="xl" pulse data-id="element-94">
              SÍ, QUIERO TODO ESTO AHORA
            </CtaButton>
          </div>
        </div>
      </section>

      {/* TRANSFORMATION SECTION */}
      <section className="py-24 sm:py-32" data-id="element-95">
        <div className="mx-auto max-w-5xl px-6 lg:px-8" data-id="element-96">
          <Reveal data-id="element-97">
            <h2 className="text-center font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-16" data-id="element-98">
              Así cambiará tu práctica clínica
            </h2>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2" data-id="element-99">
            <Reveal delay={0.1} data-id="element-100">
              <div className="rounded-3xl bg-red-50 p-8 ring-1 ring-red-100" data-id="element-101">
                <h3 className="text-xl font-bold text-red-900 mb-6 flex items-center gap-2" data-id="element-102">
                  <XCircle className="h-6 w-6" data-id="element-103" /> ANTES
                </h3>
                <ul className="space-y-4" data-id="element-104">
                  {['Improvisar', 'Dudar', 'Bloquearse', 'Preparar sesiones durante horas', 'Sentirse inseguro'].map((item, i) => <li key={i} className="flex items-center gap-3 text-red-800" data-id="element-105">
                      <XCircle className="h-5 w-5 shrink-0 opacity-50" data-id="element-106" /> {item}
                    </li>)}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2} data-id="element-107">
              <div className="rounded-3xl bg-green-50 p-8 ring-1 ring-green-100 shadow-float" data-id="element-108">
                <h3 className="text-xl font-bold text-green-900 mb-6 flex items-center gap-2" data-id="element-109">
                  <CheckCircle2 className="h-6 w-6" data-id="element-110" /> DESPUÉS
                </h3>
                <ul className="space-y-4" data-id="element-111">
                  {['Mayor seguridad', 'Intervenciones claras', 'Sesiones fluidas', 'Más confianza', 'Herramientas listas'].map((item, i) => <li key={i} className="flex items-center gap-3 text-green-800 font-medium" data-id="element-112">
                      <CheckCircle2 className="h-5 w-5 shrink-0" data-id="element-113" /> {item}
                    </li>)}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="bg-brand-purple py-24 sm:py-32 text-white" data-id="element-114">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center" data-id="element-115">
          <Reveal data-id="element-116">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl mb-6" data-id="element-117">
              ¿Por qué funciona?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-brand-rosa mb-16" data-id="element-118">
              Mientras la mayoría de libros enseñan teoría…
              <br data-id="element-119" />
              <strong className="text-white" data-id="element-120">
                Este sistema fue creado para ayudarte a decidir qué hacer en
                tiempo real.
              </strong>
            </p>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3 max-w-4xl mx-auto relative" data-id="element-121">
            <div className="hidden md:block absolute top-1/2 left-1/6 right-1/6 h-0.5 bg-brand-lila/30 -z-10" data-id="element-122" />
            {[{
            step: '1',
            title: 'Diagnóstico rápido'
          }, {
            step: '2',
            title: 'Pregunta correcta'
          }, {
            step: '3',
            title: 'Intervención correcta'
          }].map((item, i) => <Reveal key={i} delay={i * 0.1} data-id="element-123">
                <div className="flex flex-col items-center" data-id="element-124">
                  <div className="w-16 h-16 rounded-full bg-white text-brand-purple flex items-center justify-center text-2xl font-bold mb-6 shadow-lg" data-id="element-125">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold" data-id="element-126">{item.title}</h3>
                  {i < 2 && <div className="md:hidden text-brand-lila mt-6" data-id="element-127">↓</div>}
                </div>
              </Reveal>)}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="py-24 sm:py-32 bg-gray-50" data-id="element-128">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-129">
          <Reveal data-id="element-130">
            <h2 className="text-center font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-16" data-id="element-131">
              Ideal para
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-id="element-132">
            {['Psicólogos Clínicos', 'Psicoterapeutas', 'ACT', 'DBT', 'TCC', 'Terapia Integrativa', 'Estudiantes', 'Psicólogos recién egresados'].map((item, i) => <Reveal key={i} delay={i * 0.05} data-id="element-133">
                <div className="flex items-center justify-center rounded-2xl bg-white p-6 text-center font-semibold text-brand-purple shadow-sm ring-1 ring-gray-200 hover:ring-brand-lila transition-colors" data-id="element-134">
                  {item}
                </div>
              </Reveal>)}
          </div>
        </div>
      </section>

      {/* EVERYTHING INCLUDED */}
      <section className="py-24 sm:py-32" data-id="element-135">
        <div className="mx-auto max-w-3xl px-6 lg:px-8" data-id="element-136">
          <Reveal data-id="element-137">
            <div className="rounded-4xl bg-white p-8 sm:p-12 shadow-float ring-1 ring-gray-100" data-id="element-138">
              <h2 className="text-center font-display text-3xl font-bold tracking-tight text-gray-900 mb-10" data-id="element-139">
                Todo incluido
              </h2>
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-140">
                {['Más de 30 protocolos', 'Más de 200 preguntas', 'Más de 120 scripts', 'Frameworks', 'Árboles de decisión', 'Checklists', 'Recursos imprimibles', 'Tareas para casa'].map((item, i) => <div key={i} className="flex items-center gap-3 text-lg text-gray-700" data-id="element-141">
                    <CheckCircle2 className="h-6 w-6 text-brand-purple shrink-0" data-id="element-142" />
                    {item}
                  </div>)}
              </div>
              <div className="mt-12 text-center" data-id="element-143">
                <CtaButton size="lg" fullWidth data-id="element-144">
                  ACCEDER A TODO EL MATERIAL
                </CtaButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-brand-soft py-24 sm:py-32 overflow-hidden" data-id="element-145">
        <div className="mx-auto max-w-7xl px-6 lg:px-8" data-id="element-146">
          <Reveal data-id="element-147">
            <h2 className="text-center font-display text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-16" data-id="element-148">
              Lo que dicen otros terapeutas
            </h2>
          </Reveal>

          <div className="flex flex-col gap-6 sm:flex-row sm:overflow-x-auto sm:pb-8 sm:snap-x sm:snap-mandatory hide-scrollbar" data-id="element-149">
            {[{
            name: 'Lic. Andrea M.',
            role: 'Psicóloga Clínica',
            avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
            text: '“Antes sentía que algunas sesiones se quedaban estancadas y terminaba improvisando. Ahora solo consulto el protocolo y encuentro rápidamente una intervención que puedo aplicar. Me ha dado mucha más seguridad en consulta.”'
          }, {
            name: 'Carlos R.',
            role: 'Psicoterapeuta',
            avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
            text: '“Lo que más me gustó fueron los scripts y las preguntas terapéuticas. Me han ayudado a conducir sesiones difíciles con mucha más claridad y confianza.”'
          }, {
            name: 'María Fernanda G.',
            role: 'Estudiante de Psicología',
            avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
            text: '“Estoy realizando mis primeras prácticas clínicas y este material se ha convertido en mi guía de apoyo antes de cada sesión. Es muy práctico y fácil de consultar.”'
          }, {
            name: 'Dra. Sofía P.',
            role: 'Psicóloga Cognitivo Conductual',
            avatar: 'https://randomuser.me/api/portraits/women/29.jpg',
            text: '“No reemplaza la formación clínica, pero sí facilita muchísimo el proceso de decidir qué hacer cuando una sesión se bloquea. Lo recomiendo totalmente.”'
          }, {
            name: 'José A.',
            role: 'Terapeuta ACT',
            avatar: 'https://randomuser.me/api/portraits/men/54.jpg',
            text: '“Es uno de esos recursos que siempre quiero tener abierto durante mis consultas. Ahorra muchísimo tiempo y aporta ideas muy útiles.”'
          }].map((test, i) => <Reveal key={i} delay={i * 0.1} className="snap-center shrink-0 w-full sm:w-[400px]" data-id="element-150">
                <div className="flex h-full flex-col rounded-3xl bg-white p-8 shadow-card" data-id="element-151">
                  <div className="flex text-brand-gold mb-4" data-id="element-152">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-current" data-id="element-153" />)}
                  </div>
                  <p className="text-gray-600 mb-6 italic" data-id="element-154">{test.text}</p>
                  <div className="flex items-center gap-4 mt-auto" data-id="element-155">
                    <img src={test.avatar} alt={test.name} loading="lazy" className="h-12 w-12 rounded-full object-cover ring-2 ring-brand-rosa" data-id="element-156" />
                    <div data-id="element-157">
                      <h4 className="font-bold text-gray-900" data-id="element-158">{test.name}</h4>
                      <p className="text-sm text-gray-500" data-id="element-159">{test.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>)}
          </div>

          <Reveal delay={0.4} data-id="element-160">
            <div className="mt-12 mx-auto max-w-2xl rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-200" data-id="element-161">
              <div className="flex justify-center text-brand-gold mb-2" data-id="element-162">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-6 w-6 fill-current" data-id="element-163" />)}
              </div>
              <p className="font-medium text-gray-900" data-id="element-164">
                Más de 2,000 terapeutas ya utilizan este sistema para
                complementar su práctica clínica.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFER SECTION */}
      <section className="py-24 sm:py-32 relative" data-id="element-165">
        <div className="absolute inset-0 bg-brand-purple/5 -z-10" data-id="element-166" />
        <div className="mx-auto max-w-3xl px-6 lg:px-8" data-id="element-167">
          <Reveal data-id="element-168">
            <div className="rounded-4xl bg-white shadow-float ring-4 ring-brand-purple/20 overflow-hidden" data-id="element-169">
              <div className="bg-brand-purple py-6 text-center" data-id="element-170">
                <h2 className="font-display text-3xl font-bold text-white" data-id="element-171">
                  Oferta Especial de Hoy
                </h2>
              </div>
              <div className="p-8 sm:p-12 text-center" data-id="element-172">
                <div className="flex justify-center items-center gap-4 mb-8" data-id="element-173">
                  <span className="text-2xl text-gray-400 line-through decoration-red-500 decoration-2" data-id="element-174">
                    US$97
                  </span>
                  <span className="text-6xl font-extrabold text-green-600" data-id="element-175">
                    US$10.99
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-10 max-w-md mx-auto text-left" data-id="element-176">
                  <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-177">
                    <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-178" /> Pago
                    único
                  </div>
                  <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-179">
                    <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-180" /> Acceso
                    inmediato
                  </div>
                  <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-181">
                    <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-182" /> Descarga
                    permanente
                  </div>
                  <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-183">
                    <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-184" /> Sin
                    suscripciones
                  </div>
                </div>

                <CtaButton size="xl" pulse fullWidth className="text-xl sm:text-2xl py-6" data-id="element-185">
                  QUIERO MI ACCESO AHORA
                </CtaButton>
                <img src={IMAGES.principal} alt="Protocolo" className="w-48 mx-auto mt-8 drop-shadow-xl cursor-pointer" onClick={goToCheckout} data-id="element-186" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 bg-white" data-id="element-187">
        <div className="mx-auto max-w-3xl px-6 lg:px-8" data-id="element-188">
          <Reveal data-id="element-189">
            <div className="flex flex-col sm:flex-row items-center gap-8 rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-200" data-id="element-190">
              <ShieldCheck className="h-24 w-24 text-brand-gold shrink-0" data-id="element-191" />
              <div data-id="element-192">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-193">
                  Compra con tranquilidad.
                </h3>
                <p className="text-gray-600" data-id="element-194">
                  Si durante los próximos 7 días consideras que este sistema no
                  aporta valor a tu práctica clínica, puedes solicitar la
                  devolución de tu dinero.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-32 bg-gray-50" data-id="element-195">
        <div className="mx-auto max-w-3xl px-6 lg:px-8" data-id="element-196">
          <Reveal data-id="element-197">
            <h2 className="text-center font-display text-3xl font-bold tracking-tight text-gray-900 mb-12" data-id="element-198">
              Preguntas Frecuentes
            </h2>
            <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200" data-id="element-199">
              {[{
              q: '¿Es para estudiantes?',
              a: 'Sí.'
            }, {
              q: '¿Sirve para ACT?',
              a: 'Sí.'
            }, {
              q: '¿Sirve para DBT?',
              a: 'Sí.'
            }, {
              q: '¿Sirve para TCC?',
              a: 'Sí.'
            }, {
              q: '¿Cómo recibiré el material?',
              a: 'Acceso inmediato después del pago.'
            }, {
              q: '¿Necesito experiencia?',
              a: 'No.'
            }, {
              q: '¿Es físico?',
              a: 'No. Todo el material es digital.'
            }, {
              q: '¿Puedo descargarlo para siempre?',
              a: 'Sí.'
            }].map((faq, i) => <AccordionItem key={i} question={faq.q} answer={faq.a} data-id="element-200" />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-purple via-[#4b32bd] to-brand-purple py-32 text-center" data-id="element-201">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-202" />
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8" data-id="element-203">
          <Reveal data-id="element-204">
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-6xl mb-6" data-id="element-205">
              Nunca más vuelvas a entrar a una sesión sin saber qué hacer.
            </h2>
            <p className="text-xl text-brand-rosa mb-12 max-w-2xl mx-auto" data-id="element-206">
              Empieza hoy mismo a intervenir con más claridad, confianza y
              seguridad.
            </p>

            <img src={IMAGES.principal} alt="Protocolo" className="w-64 mx-auto mb-12 drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-500" onClick={goToCheckout} data-id="element-207" />

            <button onClick={goToCheckout} className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-white px-10 py-6 text-2xl font-extrabold text-brand-purple shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all hover:scale-105 hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] active:scale-100 cta-pulse" data-id="element-208">
              QUIERO EL SISTEMA CLÍNICO AHORA
            </button>

            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm font-medium text-brand-rosa" data-id="element-209">
              <span className="flex items-center gap-2" data-id="element-210">
                <ShieldCheck className="h-5 w-5" data-id="element-211" /> Compra 100% segura
              </span>
              <span className="flex items-center gap-2" data-id="element-212">
                <Clock className="h-5 w-5" data-id="element-213" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-2" data-id="element-214">
                <CheckCircle2 className="h-5 w-5" data-id="element-215" /> Pago único de US$10.99
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-12" data-id="element-216">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 flex flex-col items-center" data-id="element-217">
          <img src={IMAGES.logo} alt="Dra. Karito" className="h-24 w-auto mb-6 opacity-80 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-218" />
          <p className="text-sm text-gray-500 mb-6" data-id="element-219">
            © 2026 Dra. Karito. Todos los derechos reservados.
          </p>
          <div className="flex gap-6 text-sm text-gray-400" data-id="element-220">
            <a href="#" className="hover:text-brand-purple transition-colors" data-id="element-221">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-brand-purple transition-colors" data-id="element-222">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-brand-purple transition-colors" data-id="element-223">
              Contacto
            </a>
          </div>
        </div>
      </footer>
    </div>;
}